import {
  API_COLORIZE,
  API_EDIT_LOGO,
  IMAGE_CROP_GRIP_SIZE,
  MIN_CANVAS_DPI,
  SIDEBAR_IMAGE_WIDTH_PERCENT,
  colorType,
  elementStatus,
  imageSharpenStatus,
  logoEditingStatusEnum,
  sidebarTabKeyEnum,
  stepStatus,
  undoRedoType,
  userImageType,
} from '../utils/constants';
import {
  adjustImageSizeToFitContainer,
  consoleLog,
  limitAndRepositionElement,
  switchToSharpened,
  switchToUnColorize,
} from '../canvas/logics';
import {
  changeArtColor,
  changeDirection,
  changeImageUiStatus,
  changeLogoColors,
  deleteImage,
  saveImageOriginColors,
  selectImage,
  selectImageForNonDigital,
  updateCanvasImage,
} from '../canvas/actions';
import {
  changeLogoColorAsync,
  changeTextColorAsync,
  pmsColorValidation,
  tooManyColorsExceptionCheck,
} from '../imprintarea/logics';
import {
  changeLogoEditingStatus,
  hideTransparencyCover,
  hideWaitingCover,
  removeTempBackgroundImage,
  setNewUploadLogoFlag,
  showMessageBox,
  showTransparencyCover,
  showWaitingCover,
  updateWaitingCover,
} from '../main/actions';
import { changeNextgenUiStatus, saveImagePmsColors } from '../nextgen/actions';
import { doSharpen, hideCover, startProgressChecking } from './components/UploadButton/logics';
import { getImprintColors, getImprintOptionMaxColorCount } from '../imprintarea/selectors';
import { getItemUseScene7Template, getStudioItemType, getVendorId } from '../main/selectors';
import { messageBoxButtons, studioItemType as studioItemTypeEnum } from '../main/constants';
import { nextGenFlyoutPanelEnum, nextgenScreenMode } from '../nextgen/constants';

import { applyNoticeToggle } from '../userimage/actions';
import { changeLogoStatus } from '../canvas/actions';
import { changeRemoveWhite } from '../userimage/components/EditUpload/logics';
import { checkIsLaserProduct } from '../utils/processTypeUtil';
import { editBackgroundFinalize } from './components/DesktopBackgroundCrop/logics';
import { getDeviceViewPort } from '../utils/device';
import { getScene7Url } from '../utils/scene7url';
import { getScreenMode } from '../nextgen/logics';
import { isMobileOnly } from 'react-device-detect';
import { isVectorImage } from '../utils/ImageUtils';
import { mobileStep2GuideNext } from '../main/logics';
import { post } from '../utils/request';
import { pushToUndoList } from '../canvas/components/UndoRedo/logic';
import { resetToOriginal } from '../userimage/components/EditUpload/logics';
import { updateFullPreview } from '../preview/logics';
import { v4 as uuidv4 } from 'uuid';

export const detectPmsColors = ({ dispatch, canvasKey, vendorId, image, guid, callback }) => {
  if (!image.removeWhiteResourceAsIs) return;

  const imageUrl = getScene7Url(
    image.removeWhiteResourceAsIs,
    { width: image.originalWidth, height: image.originalHeight },
    null,
    true
  );

  const img = new Image();
  img.crossOrigin = 'anonymous'; // Handle CORS for external images
  img.onload = async () => {
    const mainColors = extractDominantColors(img, {
      width: image.originalWidth,
      height: image.originalHeight,
    });
    const colorList = mainColors.map((color) => ({ colorReference: color }));
    const pmsColors = await colorizeForPms(dispatch, image, vendorId, canvasKey, guid, colorList);
    if (callback) callback(pmsColors);
  };
  img.src = imageUrl;
};

const colorizeForPms = async (dispatch, image, vendorId, canvasKey, guid, domainColorList) => {
  const url =
    API_COLORIZE +
    '/colorizeForPms?' +
    ('&vendorId=' + vendorId) +
    ('&imagePath=' + encodeURIComponent(image.pngFileNameOfNoColorize));
  try {
    const res = await post(url, domainColorList);
    const pmsColors = res.data.map((item) => {
      return {
        colorReference: item.colorReference,
        colorId: item.colorId,
        colorType: colorType.Pms,
        colorDescription: `PMS Color Match: ${item.colorId}`,
      };
    });
    dispatch(saveImagePmsColors({ canvasKey, imageId: guid, pmsColors }));
    return pmsColors;
  } catch (err) {
    consoleLog(err);
    return null;
  }
};

export const colorize = (colorizeParams) => {
  colorizeParams.frontId = uuidv4();

  colorizeParams.fromColorize = true;
  const options =
    colorizeParams.currentColorCount && colorizeParams.currentColorCount > 0
      ? { apiMethod: '/colorizecombine', currentColorCount: colorizeParams.currentColorCount }
      : colorizeParams.colorsInUse !== undefined
      ? { apiMethod: '/colorizeandchangecolor', colorsInUse: colorizeParams.colorsInUse }
      : { apiMethod: '/colorize' };

  const replaceColor = colorizeParams.replaceColor != undefined ? colorizeParams.replaceColor : false;
  const url =
    API_COLORIZE +
    options.apiMethod +
    ('?frontId=' + colorizeParams.frontId) +
    ('&vendorId=' + colorizeParams.vendorId) +
    ('&imagePath=' + encodeURIComponent(colorizeParams.image.data.pngFileName)) +
    ('&maxColorCount=' + colorizeParams.maxColorCount) +
    (options.currentColorCount ? '&currentColorCount=' + options.currentColorCount : '') +
    (colorizeParams.colorsInUse !== undefined
      ? '&colorsInUse=' + colorizeParams.colorsInUse + '&replaceColor=' + replaceColor
      : '');

  post(url, colorizeParams.imprintColorList)
    .then((res) => {
      startProgressChecking(colorizeParams);
    })
    .catch((err) => {
      consoleLog(err);
      colorizeParams.dispatch(hideWaitingCover());
      colorizeParams.dispatch(showMessageBox(err.message));
    });
};

export const changeColorOrNumber = (colorizeParams) => {
  colorizeParams.frontId = uuidv4();
  colorizeParams.fromColorize = true;
  const noSharpen = !(colorizeParams.image.data.sharpenStatus === imageSharpenStatus.Sharpened);
  const image = Object.assign({}, colorizeParams.image);
  image.data = Object.assign({}, image.data);
  colorizeParams.image = image;
  if (!noSharpen && colorizeParams.studioItemType === studioItemTypeEnum.screenPrintedDigital) {
    switchToUnColorize(image);
  }
  
  let colorList = colorizeParams.colorList;
  //always attach imprintColorList
  colorList = [...colorList, null, ...colorizeParams.imprintColorList];
  
  let imagingServiceSessionId = colorizeParams.image.data.imagingServiceSessionIdOfNoColorize;
  if (!imagingServiceSessionId)
    imagingServiceSessionId = noSharpen ? colorizeParams.image.data.imagingServiceSessionId : '';

  if (!colorizeParams.isChangeNumber) {
    //always empty so change the colors based on pngFileNameOfNoColorize
    imagingServiceSessionId = '';
  }

  post(
    API_COLORIZE +
      (colorizeParams.isChangeNumber ? '/changecolornumber' : '/changecolor') +
      '?frontId=' +
      colorizeParams.frontId +
      '&vendorId=' +
      colorizeParams.vendorId +
      '&imagingServiceSessionId=' +
      imagingServiceSessionId +
      '&pngFileName=' +
      colorizeParams.image.data.pngFileNameOfNoColorize || colorizeParams.image.data.pngFileName,
    colorList
  )
    .then((res) => {
      startProgressChecking(colorizeParams);
    })
    .catch((err) => {
      consoleLog(err);
      colorizeParams.dispatch(hideWaitingCover());
      colorizeParams.dispatch(showMessageBox(err.message));
    });

  colorizeParams.dispatch(showWaitingCover({ showProgress: false }));
};

export const changeLogoColorOnTextColorChangedAsync =
  ({ imprintAreaKey, newColor }) =>
  (dispatch, getState) => {
    const state = getState();
    const studioItemType = getStudioItemType(state);
    const maxColorCount = getImprintOptionMaxColorCount(state);
    const vendorId = getVendorId(state);
    const imprintColorList = getImprintColors(state, imprintAreaKey);
    const itemUseScene7Template = getItemUseScene7Template(state);
    const logo =
      state.imageDict &&
      state.imageDict[imprintAreaKey] &&
      state.imageDict[imprintAreaKey].find((x) => x.data.type === userImageType.UploadLogo);

    if (studioItemType !== studioItemTypeEnum.digital && maxColorCount === 1 && logo && logo.data.colors.length === 1) {
      dispatch(() => {
        changeColorOrNumber({
          dispatch,
          canvasKey: imprintAreaKey,
          vendorId,
          imprintColorList,
          image: logo,
          colorList: [newColor],
          isChangeNumber: false,
          studioItemType,
          itemUseScene7Template,
        });
      });
    }
  };

// export const getColorsByColorNumber = (colorizeParams) => {
//   get(
//     API_COLORIZE +
//       '/GetColorsByColorNumber' +
//       '?imagingServiceSessionId=' +
//       colorizeParams.imageData.imagingServiceSessionId +
//       '&colorCount=' +
//       colorizeParams.colorCount
//   )
//     .then((res) => {
//       colorizeParams.dispatch(hideWaitingCover());
//       if (colorizeParams.onComplete) colorizeParams.onComplete(res.data);
//     })
//     .catch((err) => {
//       consoleLog(err);
//       colorizeParams.dispatch(hideWaitingCover());
//       colorizeParams.dispatch(showMessageBox(err.message));
//     });

//   colorizeParams.dispatch(showWaitingCover({ showProgress: false }));
// };

export const generateStepDataBlock = (logoEditingStatus) => {
  const activeStep = logoEditingStatus.currentStatus;
  const firstStep = logoEditingStatus.steps[0];
  let colorizeStepText;
  let textOffsetList2;
  let textOffsetList31;
  let textOffsetList32;
  let textOffsetList4;
  let barLengthList;

  if (isMobileOnly) {
    colorizeStepText = 'Color Detection';
    textOffsetList2 = [-25, 30];
    textOffsetList31 = [-60, -25, 30];
    textOffsetList32 = [-25, 30, 60];
    textOffsetList4 = [-30, -15, 20, 30];
    barLengthList = [80, 80];
  } else {
    colorizeStepText = ['Color Detection', '& Editing'];
    textOffsetList2 = [-35, 40];
    textOffsetList31 = [-75, -25, 45];
    textOffsetList32 = [-51, 22, 90];
    textOffsetList4 = [-40, 0, 52, 60];
    barLengthList = [80, 120];
  }

  return logoEditingStatus.steps.length === 2
    ? [
        {
          text: 'Crop',
          textOffset: textOffsetList2[0],
          active: activeStep === logoEditingStatusEnum.NonDigitalLogoCrop ? true : false,
          status: activeStep === logoEditingStatusEnum.NonDigitalLogoCrop ? stepStatus.Active : stepStatus.Done,
        },
        {
          text: 'Rotate',
          textOffset: textOffsetList2[1],
          active: activeStep === logoEditingStatusEnum.NonDigitalLogoRotate ? true : false,
          status: activeStep === logoEditingStatusEnum.NonDigitalLogoRotate ? stepStatus.Active : stepStatus.ToDo,
          barLength: 80,
        },
      ]
    : logoEditingStatus.steps.length === 3
    ? firstStep === logoEditingStatusEnum.NonDigitalLogoRemoveWhite
      ? [
          {
            text: 'Remove White',
            textOffset: textOffsetList31[0],
            active: activeStep === logoEditingStatusEnum.NonDigitalLogoRemoveWhite ? true : false,
            status:
              activeStep === logoEditingStatusEnum.NonDigitalLogoRemoveWhite ? stepStatus.Active : stepStatus.Done,
          },
          {
            text: 'Crop',
            textOffset: textOffsetList31[1],
            active: activeStep === logoEditingStatusEnum.NonDigitalLogoCrop ? true : false,
            status:
              activeStep === logoEditingStatusEnum.NonDigitalLogoCrop
                ? stepStatus.Active
                : activeStep === logoEditingStatusEnum.NonDigitalLogoRemoveWhite
                ? stepStatus.ToDo
                : stepStatus.Done,
            barLength: 80,
          },
          {
            text: 'Rotate',
            textOffset: textOffsetList31[2],
            active: activeStep === logoEditingStatusEnum.NonDigitalLogoRotate ? true : false,
            status: activeStep === logoEditingStatusEnum.NonDigitalLogoRotate ? stepStatus.Active : stepStatus.ToDo,
            barLength: 80,
          },
        ]
      : [
          {
            text: 'Crop',
            textOffset: textOffsetList32[0],
            active: activeStep === logoEditingStatusEnum.NonDigitalLogoCrop ? true : false,
            status: activeStep === logoEditingStatusEnum.NonDigitalLogoCrop ? stepStatus.Active : stepStatus.Done,
          },
          {
            text: 'Rotate',
            textOffset: textOffsetList32[1],
            active: activeStep === logoEditingStatusEnum.NonDigitalLogoRotate ? true : false,
            status:
              activeStep === logoEditingStatusEnum.NonDigitalLogoRotate
                ? stepStatus.Active
                : activeStep === logoEditingStatusEnum.NonDigitalLogoCrop
                ? stepStatus.ToDo
                : stepStatus.Done,
            barLength: barLengthList[0],
          },
          {
            text: colorizeStepText,
            textOffset: textOffsetList32[2],
            active: activeStep === logoEditingStatusEnum.NonDigitalLogoColor ? true : false,
            status: activeStep === logoEditingStatusEnum.NonDigitalLogoColor ? stepStatus.Active : stepStatus.ToDo,
            barLength: barLengthList[1],
          },
        ]
    : //logoEditingStatus.steps.length = 4
      [
        {
          text: 'Remove White',
          textOffset: textOffsetList4[0],
          active: activeStep === logoEditingStatusEnum.NonDigitalLogoRemoveWhite ? true : false,
          status: activeStep === logoEditingStatusEnum.NonDigitalLogoRemoveWhite ? stepStatus.Active : stepStatus.Done,
        },
        {
          text: 'Crop',
          textOffset: textOffsetList4[1],
          active: activeStep === logoEditingStatusEnum.NonDigitalLogoCrop ? true : false,
          status:
            activeStep === logoEditingStatusEnum.NonDigitalLogoCrop
              ? stepStatus.Active
              : activeStep === logoEditingStatusEnum.NonDigitalLogoRemoveWhite
              ? stepStatus.ToDo
              : stepStatus.Done,
          barLength: 60,
        },
        {
          text: 'Rotate',
          textOffset: textOffsetList4[2],
          active: activeStep === logoEditingStatusEnum.NonDigitalLogoRotate ? true : false,
          status:
            activeStep === logoEditingStatusEnum.NonDigitalLogoRotate
              ? stepStatus.Active
              : activeStep === logoEditingStatusEnum.NonDigitalLogoColor
              ? stepStatus.Done
              : stepStatus.ToDo,
          barLength: 60,
        },
        {
          text: colorizeStepText,
          textOffset: textOffsetList4[3],
          active: activeStep === logoEditingStatusEnum.NonDigitalLogoColor ? true : false,
          status: activeStep === logoEditingStatusEnum.NonDigitalLogoColor ? stepStatus.Active : stepStatus.ToDo,
          barLength: 60,
        },
      ];
};

export const generateStepDataBlockForBackground = (logoEditingStatus, itemUseScene7Template, isNextgen) => {
  const activeStep = logoEditingStatus.currentStatus;
  let steps;
  if (activeStep === logoEditingStatusEnum.BackgroundRemoveWhite) {
    if (isMobileOnly && !isNextgen) {
      if (itemUseScene7Template) {
        steps = [
          { text: 'Remove White', textOffset: -65, active: true },
          { text: 'Crop', textOffset: -25, active: false, barLength: 80 },
          { text: 'Rotate', textOffset: 30, active: false, barLength: 80 },
        ];
      } else {
        steps = [
          { text: 'Remove White', textOffset: -65, active: true },
          { text: 'Rotate', textOffset: -25, active: false, barLength: 80 },
          { text: 'Crop', textOffset: 30, active: false, barLength: 80 },
        ];
      }
    } else {
      if (itemUseScene7Template) {
        steps = [
          { text: 'Remove White', textOffset: -70, status: stepStatus.Active },
          { text: 'Crop', textOffset: -27, status: stepStatus.ToDo, barLength: 80 },
          { text: 'Rotate', textOffset: 46, status: stepStatus.ToDo, barLength: 80 },
        ];
      } else {
        steps = [
          { text: 'Remove White', textOffset: -70, status: stepStatus.Active },
          { text: 'Rotate', textOffset: -30, status: stepStatus.ToDo, barLength: 80 },
          { text: 'Crop', textOffset: 40, status: stepStatus.ToDo, barLength: 80 },
        ];
      }
    }
  } else if (activeStep === logoEditingStatusEnum.BackgroundCrop) {
    if (isMobileOnly && !isNextgen) {
      if (itemUseScene7Template) {
        steps = [
          { text: 'Remove White', textOffset: -65, active: false },
          { text: 'Crop', textOffset: -25, active: true, barLength: 80 },
          { text: 'Rotate', textOffset: 30, active: false, barLength: 80 },
        ];
      } else {
        steps = [
          { text: 'Remove White', textOffset: -65, active: false },
          { text: 'Rotate', textOffset: -25, active: false, barLength: 80 },
          { text: 'Crop', textOffset: 30, active: true, barLength: 80 },
        ];
      }
    } else {
      if (itemUseScene7Template) {
        steps = [
          { text: 'Remove White', textOffset: -70, status: stepStatus.Done },
          { text: 'Crop', textOffset: -25, status: stepStatus.Active, barLength: 80 },
          { text: 'Rotate', textOffset: 46, status: stepStatus.ToDo, barLength: 80 },
        ];
      } else {
        steps = [
          { text: 'Remove White', textOffset: -70, status: stepStatus.Done },
          { text: 'Rotate', textOffset: -30, status: stepStatus.Done, barLength: 80 },
          { text: 'Crop', textOffset: 40, status: stepStatus.Active, barLength: 80 },
        ];
      }
    }
  } else if (activeStep === logoEditingStatusEnum.BackgroundRotate) {
    if (isMobileOnly && !isNextgen) {
      if (itemUseScene7Template) {
        steps = [
          { text: 'Remove White', textOffset: -65, active: false },
          { text: 'Crop', textOffset: -25, active: false, barLength: 80 },
          { text: 'Rotate', textOffset: 30, active: true, barLength: 80 },
        ];
      } else {
        steps = [
          { text: 'Remove White', textOffset: -65, active: false },
          { text: 'Rotate', textOffset: -25, active: true, barLength: 80 },
          { text: 'Crop', textOffset: 30, active: false, barLength: 80 },
        ];
      }
    } else {
      if (itemUseScene7Template) {
        steps = [
          { text: 'Remove White', textOffset: -70, status: stepStatus.Done },
          { text: 'Crop', textOffset: -24, status: stepStatus.Done, barLength: 80 },
          { text: 'Rotate', textOffset: 46, status: stepStatus.Active, barLength: 80 },
        ];
      } else {
        steps = [
          { text: 'Remove White', textOffset: -70, status: stepStatus.Done },
          { text: 'Rotate', textOffset: -30, status: stepStatus.Active, barLength: 80 },
          { text: 'Crop', textOffset: 40, status: stepStatus.ToDo, barLength: 80 },
        ];
      }
    }
  }
  return steps;
};

export const setLogoEditingStatusToPreview = (studioItemType, imageDict, dispatch) => {
  Object.keys(imageDict).forEach((key) => {
    if (imageDict[key] && imageDict[key].length > 0) {
      imageDict[key].forEach((image) => {
        if (image.type === userImageType.UploadLogo) {
          if (studioItemType === studioItemTypeEnum.screenPrintedDigital) {
            if (!isMobileOnly) dispatch(changeLogoEditingStatus(logoEditingStatusEnum.ScreenPrintedEditUpload));
          } else {
            dispatch(changeLogoEditingStatus(logoEditingStatusEnum.NonDigitalLogoPreview));
          }
        }
      });
    }
  });
};

export const isLogoEditing = (editingStatus) => {
  if (
    editingStatus !== logoEditingStatusEnum.None &&
    editingStatus !== logoEditingStatusEnum.LogoCropping &&
    editingStatus !== logoEditingStatusEnum.NonDigitalLogoPreview &&
    editingStatus !== logoEditingStatusEnum.NonDigitalLogoAsRecent &&
    editingStatus !== logoEditingStatusEnum.ScreenPrintedEditUpload &&
    editingStatus !== nextGenFlyoutPanelEnum.EditImagePanel
  )
    return true;
  else return false;
};

export const isDpiWarning = (editingStatus) => {
  if (
    editingStatus === logoEditingStatusEnum.DpiWarningPanel ||
    editingStatus === logoEditingStatusEnum.DpiWarningButton ||
    editingStatus === logoEditingStatusEnum.DpiWarningContinueToCart
  )
    return true;
  else return false;
};

export const updateLogoColorType = (logoData, sourceColorList) => {
  // update the colorType in colors
  const newLogoData = Object.assign({}, logoData);
  delete newLogoData.colors;
  newLogoData.colors = [];

  logoData.colors.forEach((color, index) => {
    newLogoData.colors[index] = Object.assign({}, color);
    newLogoData.colors[index].colorType = sourceColorList.find((c) => c.colorId === color.colorId).colorType;
  });

  return newLogoData;
};

export const doColorize = (params) => {
  const {
    dispatch,
    canvasKey,
    imprintColorList,
    selectedLogo,
    logoEditingStatus,
    studioItemType,
    processType,
    isNextgenUi,
  } = params;

  const isLaserProduct = checkIsLaserProduct(processType);

  // M1082: using backend colorize service, so no need the following logic
  // if (params.hasOneImprintColorOnly) {
  //   // no color detection step, directly change color and go to LogoPreview/EditUpload(screenprinted)
  //   dispatch(
  //     changeLogoColorAsync(
  //       {
  //         formatkey: canvasKey,
  //         guid: selectedLogo.ui.guid,
  //         colors: imprintColorList,
  //       },
  //       () => {
  //         // --- laser product has only one imprint color choice, so no need Color Detection step
  //         // --- TODO: for other procut with the same situation, we will do the same thing
  //         if (studioItemType === studioItemTypeEnum.screenPrintedDigital && isLaserProduct) {
  //           dispatch(selectImage({ canvasKey, imageId: selectedLogo.ui.guid }));
  //           setLogoEditingStatusForColorize(dispatch, studioItemType, true, isNextgenUi);
  //         } else {
  //           dispatch(
  //             selectImageForNonDigital({ canvasKey, sidebarTabKey: sidebarTabKeyEnum.Logo, guid: selectedLogo.ui.guid })
  //           );
  //           setLogoEditingStatusForColorize(dispatch, studioItemType, false, isNextgenUi);
  //         }
  //         if (!isNextgenUi) {
  //           dispatch(updateFullPreview());
  //           if (isMobileOnly) dispatch(mobileStep2GuideNext());
  //         }

  //         const newLogoData = Object.assign({}, selectedLogo.data, { colors: imprintColorList });
  //         if (params.callbackAfterColorize) params.callbackAfterColorize(newLogoData);

  //         hideCover(dispatch);
  //       }
  //     )
  //   );

  //   if (studioItemType === studioItemTypeEnum.screenPrintedDigital) {
  //     dispatch(pushToUndoList(undoRedoType.UploadLogo));
  //   }
  //   return;
  // }

  const finalize = (logoData) => {
    const newLogoData = updateLogoColorType(logoData, imprintColorList);

    const afterChangeLogoColor = (newLogoData) => {
      const noColorStep =
        !logoEditingStatus.steps ||
        logoEditingStatus.steps[logoEditingStatus.steps.length - 1] !== logoEditingStatusEnum.NonDigitalLogoColor;
      if (noColorStep) {
        dispatch(
          selectImageForNonDigital({ canvasKey, sidebarTabKey: sidebarTabKeyEnum.Logo, guid: selectedLogo.ui.guid })
        );
        setLogoEditingStatusForColorize(dispatch, studioItemType, false, isNextgenUi);
      } else gotoNextStep(dispatch, logoEditingStatus, studioItemType);

      if (studioItemType === studioItemTypeEnum.screenPrintedDigital) {
        if (params.callbackAfterColorize) params.callbackAfterColorize(newLogoData);
        else dispatch(pushToUndoList(undoRedoType.UploadLogo));
      }
      dispatch(updateFullPreview());
    };

    dispatch(
      changeLogoColorAsync(
        {
          formatkey: canvasKey,
          guid: selectedLogo.ui.guid,
          colors: newLogoData.colors,
        },
        () => afterChangeLogoColor(newLogoData)
      )
    );
  };

  colorize({
    ...params,
    image: selectedLogo,
    callback: finalize,
  });
};

export const changeTextColor = (dispatch, canvasKey, newTextColor, isChangingColor) => {
  dispatch(changeTextColorAsync({ formatkey: canvasKey, color: newTextColor }));
  dispatch(updateFullPreview());

  if (isChangingColor) {
    dispatch(changeLogoEditingStatus({ color: newTextColor }));
    dispatch(changeLogoColorOnTextColorChangedAsync({ imprintAreaKey: canvasKey, newColor: newTextColor }));
  } else {
    dispatch(changeLogoEditingStatus(logoEditingStatusEnum.NonDigitalLogoColor));

    // still check and change logo color if mobile because no colorpicker panel
    if (isMobileOnly)
      dispatch(changeLogoColorOnTextColorChangedAsync({ imprintAreaKey: canvasKey, newColor: newTextColor }));
  }
};

export const changeLogoColor = (params) => {
  const {
    dispatch,
    canvasKey,
    selectedLogo,
    newColor,
    oldColor,
    isHotStamped,
    newColors, // only used for MobileLogoColor
    isChangingColor,
    callback,
    sidebarTabKey,
    studioItemType,
    logoPmsColorSelected,
    textPmsColorSelected,
    originColor,
    colorClicked,
  } = params;

  const finalize = (logoData) => {
    const newLogoData = updateLogoColorType(logoData, newColorList);

    dispatch(
      changeLogoColorAsync(
        {
          formatkey: canvasKey,
          guid: selectedLogo.ui.guid,
          colors: newLogoData.colors,
        },
        () => {
          if (isChangingColor) dispatch(changeLogoEditingStatus({ color: newColor }));

          if (callback) {
            callback({
              dispatch,
              canvasKey,
              sidebarTabKey,
              studioItemType,
              logoPmsColorSelected,
              textPmsColorSelected,
            });
          }
        }
      )
    );
  };

  let newColorList = [];
  if (newColors) {
    newColorList = newColors;
  } else {
    newColorList = [...selectedLogo.data.colors];
    if (oldColor) {
      const index = newColorList.findIndex((item) => item.colorId === oldColor.colorId);
      if (index != -1) newColorList.splice(index, 1, newColor);
    } else {
      const index = newColorList.findIndex((item) => item.colorId === colorClicked.colorId);
      newColorList.splice(index, 1, originColor);
    }
  }

  if (isHotStamped) {
    // if changing the logo color for hot-stamped items, directly use the new color
    const newLogoData = Object.assign({}, selectedLogo.data);
    newLogoData.colors = newColorList;
    finalize(newLogoData);
  } else {
    changeColorOrNumber({
      ...params,
      image: selectedLogo,
      colorList: newColorList,
      isChangeNumber: false,
      callback: finalize,
    });
  }
};

const getIconPosition = (params) => {
  const { imageAreaWidth, containerWidthHeight, iconSize, direction, itemUseScene7Template } = params;
  let imageSize = params.imageSize;

  if (itemUseScene7Template) {
    if (direction === 90 || direction === 270) {
      imageSize = { width: imageSize.height, height: imageSize.width };
    }
  }
  const imageMarginOffset = {
    x: Math.round((imageAreaWidth - containerWidthHeight) / 2),
  };

  const imageOffset = {
    x: Math.round((containerWidthHeight - imageSize.width) / 2),
    y: Math.round((containerWidthHeight - imageSize.height) / 2),
  };

  if (direction === 0) {
    return {
      top: -iconSize / 2,
      left: imageMarginOffset.x + imageOffset.x + imageSize.width - iconSize / 2,
    };
  } else if (direction === 90) {
    return {
      top: imageSize.height - iconSize / 2,
      left: imageMarginOffset.x + imageOffset.x + imageSize.width - iconSize / 2,
    };
  } else if (direction === 180) {
    return {
      top: imageSize.height - iconSize / 2,
      left: imageMarginOffset.x + imageOffset.x - iconSize / 2,
    };
  } else if (direction === 270) {
    return {
      top: -iconSize / 2,
      left: imageMarginOffset.x + imageOffset.x - iconSize / 2,
    };
  }
};

export const gotoFirstStep = (dispatch, logoEditingStatus) => {
  const firstStep = logoEditingStatus.steps[0];
  dispatch(changeLogoEditingStatus(firstStep));

  // nextgenui
  if (firstStep === logoEditingStatusEnum.NonDigitalLogoRemoveWhite)
    dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.LogoRemoveWhitePanel } }));
  else if (firstStep === logoEditingStatusEnum.NonDigitalLogoCrop)
    dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.LogoCropPanel } }));
  else if (firstStep === logoEditingStatusEnum.NonDigitalLogoRotate)
    dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.LogoRotatePanel } }));
};

export const gotoNextStep = (dispatch, logoEditingStatus, studioItemType) => {
  const nextStep = getNextStep(logoEditingStatus, studioItemType);
  dispatch(changeLogoEditingStatus(nextStep));

  // nextgenui
  if (nextStep === logoEditingStatusEnum.NonDigitalLogoCrop)
    dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.LogoCropPanel } }));
  else if (nextStep === logoEditingStatusEnum.NonDigitalLogoRotate)
    dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.LogoRotatePanel } }));
  else if (nextStep === logoEditingStatusEnum.NonDigitalLogoColor)
    dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.LogoColorPanel } }));
};

export const gotoNextBackgroundEditingStep = (
  dispatch,
  logoEditingStatus,
  canvasKey,
  customTemplate,
  backgroundImage,
  canvasData
) => {
  const steps = logoEditingStatus.steps;
  const index = steps.findIndex((step) => step === logoEditingStatus.currentStatus);

  if (index < steps.length - 1) {
    const nextStep = steps[index + 1];
    dispatch(changeLogoEditingStatus(nextStep));

    // nextgenui
    if (nextStep === logoEditingStatusEnum.BackgroundRemoveWhite)
      dispatch(
        changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.BackgroundRemoveWhitePanel } })
      );
    else if (nextStep === logoEditingStatusEnum.BackgroundCrop)
      dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.BackgroundCropPanel } }));
    else if (nextStep === logoEditingStatusEnum.BackgroundRotate)
      dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.BackgroundRotatePanel } }));
  } else editBackgroundFinalize(dispatch, canvasKey, customTemplate, backgroundImage, canvasData);
};

export const gotoPrevBackgroundEditingStep = (dispatch, logoEditingStatus) => {
  const steps = logoEditingStatus.steps;
  const index = steps.findIndex((step) => step === logoEditingStatus.currentStatus);
  const prevStep = index > 0 ? steps[index - 1] : steps[0];
  dispatch(changeLogoEditingStatus(prevStep));

  // nextgenui
  if (prevStep === logoEditingStatusEnum.BackgroundRemoveWhite)
    dispatch(
      changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.BackgroundRemoveWhitePanel } })
    );
  else if (prevStep === logoEditingStatusEnum.BackgroundCrop)
    dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.BackgroundCropPanel } }));
  else if (prevStep === logoEditingStatusEnum.BackgroundRotate)
    dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.BackgroundRotatePanel } }));
};

export const getNextStep = (logoEditingStatus, studioItemType) => {
  const steps = logoEditingStatus.steps;
  if (
    logoEditingStatus.currentStatus === logoEditingStatusEnum.DigitalLogoRemoveWhite &&
    studioItemType === studioItemTypeEnum.screenPrintedDigital &&
    isMobileOnly
  ) {
    return logoEditingStatusEnum.NonDigitalLogoColor;
  } else {
    const index = steps.findIndex((step) => step === logoEditingStatus.currentStatus);
    return index < steps.length - 1 ? steps[index + 1] : steps[index];
  }
};

export const confirmAndDeleteLogo = (dispatch, canvasKey, selectedLogo, studioItemType, currentLogoEditingStatus) => {
  dispatch(
    showMessageBox({
      buttons: messageBoxButtons.okCancel,
      buttonsCaption: ['Yes', 'No'],
      onOk: () => {
        dispatch(deleteImage({ canvasKey, guid: selectedLogo.ui.guid }));
        dispatch(changeLogoEditingStatus(logoEditingStatusEnum.None));
        dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.UploadPanel } }));
        if (studioItemType !== studioItemTypeEnum.digital) {
          dispatch(setNewUploadLogoFlag(true));
          dispatch(updateFullPreview());
        }
        if (currentLogoEditingStatus === logoEditingStatusEnum.LogoCropping) dispatch(hideTransparencyCover());
      },
      message: 'Are you sure you want to remove your logo/art?',
    })
  );
};

export const confirmAndDeleteBackground = (dispatch) => {
  dispatch(
    showMessageBox({
      buttons: messageBoxButtons.okCancel,
      buttonsCaption: ['Yes', 'No'],
      onOk: () => {
        dispatch(removeTempBackgroundImage());
        dispatch(changeLogoEditingStatus(logoEditingStatusEnum.None));
        dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.BackgroundPanel } }));
      },
      message: 'Are you sure you want to remove your background?',
    })
  );
};

export const doRemoveWhite = (dispatch, isBackground, newOption, storeData) => {
  const { selectedLogo, tempBackground, studioItemType } = storeData;

  changeRemoveWhite({
    ...storeData,
    dispatch,
    image: isBackground ? tempBackground : selectedLogo,
    newOption,
    isBackground,
  });

  if (studioItemType === studioItemTypeEnum.nonDigital) dispatch(updateFullPreview());
};

export const getCropImageContainerRect = (selectedLogo, containerSize) => {
  const size = adjustImageSizeToFitContainer(
    { width: selectedLogo.data.width, height: selectedLogo.data.height },
    { width: containerSize, height: containerSize }
  );

  const newSize = {
    width: size.width + IMAGE_CROP_GRIP_SIZE,
    height: size.height + IMAGE_CROP_GRIP_SIZE,
  };

  return {
    left: (containerSize - newSize.width) / 2,
    top: (containerSize - newSize.height) / 2,
    width: newSize.width,
    height: newSize.height,
  };
};

export const getCropInputParameters = (storeData, isBackground, itemUseScene7Template) => {
  const { sideBarWidth, canvasData, selectedLogo, tempBackground } = storeData;
  const selectedImage = isBackground ? tempBackground : selectedLogo;

  const { containerWidthHeight, imageSize } = getContainerImageSize({
    sideBarWidth,
    selectedImage,
    isBackground,
    itemUseScene7Template,
  });

  const allMargin = {
    x: canvasData.extraMarginX || 0 + canvasData.bleedLeft + canvasData.marginLeft,
    y: canvasData.extraMarginY || 0 + canvasData.bleedTop + canvasData.marginTop,
  };
  const resizeRatio = isBackground
    ? imageSize.width / selectedImage.data.width
    : imageSize.width / selectedImage.data.finalCropRect.width;

  const imagePosition = {
    left: (containerWidthHeight - imageSize.width) / 2,
    top: (containerWidthHeight - imageSize.height) / 2,
  };
  return { containerWidthHeight, allMargin, imageSize, resizeRatio, imagePosition };
};

export const getRotateInputParameters = (storeData, direction, isBackground, itemUseScene7Template) => {
  const { sideBarWidth, selectedLogo, tempBackground, logoEditingStatus } = storeData;
  const selectedImage = isBackground ? tempBackground : selectedLogo;

  const { containerWidthHeight, imageSize, imageAreaWidth } = getContainerImageSize({
    sideBarWidth,
    selectedImage,
    direction,
    isBackground,
    itemUseScene7Template,
  });

  const iconPosition = getIconPosition({
    imageAreaWidth,
    containerWidthHeight,
    imageSize,
    iconSize: 20,
    direction: selectedImage.data.direction,
    isBackground,
    itemUseScene7Template,
  });

  let hasColorStep = false;
  if (!isBackground && logoEditingStatus.steps) {
    hasColorStep =
      logoEditingStatus.steps[logoEditingStatus.steps.length - 1] === logoEditingStatusEnum.NonDigitalLogoColor;
  }

  return { imageAreaWidth, containerWidthHeight, imageSize, iconPosition, hasColorStep };
};

export const getContainerImageSize = ({
  sideBarWidth,
  selectedImage,
  direction,
  isBackground,
  itemUseScene7Template,
}) => {
  const viewPortWidth = getDeviceViewPort().width;

  //the image area takes 90% of the sidebar width(same as css definition) for desktop
  const imageAreaWidth = isMobileOnly ? Math.min(viewPortWidth * 0.8, 400) : Math.round(sideBarWidth * 0.9);

  //image container takes 80% of the image area width(the container height=width)
  const containerWidthHeight = Math.round(imageAreaWidth * SIDEBAR_IMAGE_WIDTH_PERCENT);

  let imageSize;
  if (itemUseScene7Template) {
    imageSize = adjustImageSizeToFitContainer(
      { width: selectedImage.data.finalCropRect.width, height: selectedImage.data.finalCropRect.height },
      { width: containerWidthHeight, height: containerWidthHeight }
    );
  } else {
    if (direction && !isBackground)
      imageSize = adjustImageSizeToFitContainer(
        {
          width: direction === 0 || direction === 180 ? selectedImage.data.width : selectedImage.data.height,
          height: direction === 0 || direction === 180 ? selectedImage.data.height : selectedImage.data.width,
        },
        { width: containerWidthHeight, height: containerWidthHeight }
      );
    else
      imageSize = adjustImageSizeToFitContainer(
        { width: selectedImage.data.width, height: selectedImage.data.height },
        { width: containerWidthHeight, height: containerWidthHeight }
      );
  }
  return { containerWidthHeight, imageSize, imageAreaWidth };
};

export const doRotate = ({ dispatch, direction, canvasKey, selectedLogo, studioItemType, draggingArea }) => {
  let newDirection;
  if (direction === 0) newDirection = 90;
  else if (direction === 90) newDirection = 180;
  else if (direction === 180) newDirection = 270;
  else if (direction === 270) newDirection = 0;

  dispatch(
    changeDirection({
      canvasKey,
      guid: selectedLogo.ui.guid,
      direction: newDirection,
      studioItemType,
      draggingArea,
    })
  );

  if (studioItemType === studioItemTypeEnum.screenPrintedDigital)
    limitAndRepositionElement(selectedLogo.data, draggingArea);

  dispatch(updateFullPreview());
};

export const changeColorNumber = (params) => {
  const { dispatch, canvasKey, newColorNumber, selectedLogo, callback } = params;

  const colorList = Object.assign([], selectedLogo.ui.originColors || selectedLogo.data.colors);
  const newColorList = [...colorList.slice(0, newColorNumber)];
  const oldColorNumber = selectedLogo.data.colors.length;
  if (newColorNumber < oldColorNumber) {
    newColorList.splice(0, newColorNumber, ...selectedLogo.data.colors.slice(0, newColorNumber));
    //we are sure newColorList is valid, can update immediately before calling imaging service
    dispatch(changeLogoColors({ canvasKey, imageId: selectedLogo.ui.guid, colors: newColorList }));
  } else newColorList.splice(0, oldColorNumber, ...selectedLogo.data.colors);

  changeColorOrNumber({
    ...params,
    image: selectedLogo,
    colorList: newColorList,
    isChangeNumber: true,
    callback: () => {
      dispatch(updateFullPreview());
      callback && callback();
    },
  });
};
export const checkColorChanged = (oldColorList, newColorList) => {
  let changed = false;
  if (oldColorList && oldColorList.length > 0) {
    for (var i = 0; i < oldColorList.length; i++) {
      if (
        oldColorList[i].colorType !== newColorList[i].colorType ||
        oldColorList[i].colorId !== newColorList[i].colorId ||
        oldColorList[i].colorReference !== newColorList[i].colorReference
      ) {
        changed = true;
      }
    }
  } else {
    if (newColorList && newColorList.length > 0) {
      changed = true;
    }
  }
  return changed;
};

export const checkPmsColor = ({ dispatch, colorList }) => {
  if (colorList && colorList.length > 0) {
    for (var i = 0; i < colorList.length; i++) {
      const pmsValidation = pmsColorValidation(dispatch, colorList[i]);
      if (!pmsValidation.valid) {
        dispatch &&
          dispatch(
            applyNoticeToggle({
              show: true,
              prompt: pmsValidation.message,
            })
          );
        return false;
      }
    }
  }
  return true;
};

export const mobileApplyLogoColor = (params) => {
  const { dispatch, logoColorList, textColor } = params;
  if (checkPmsColor({ dispatch, colorList: logoColorList }) && checkPmsColor({ dispatch, colorList: [textColor] })) {
    dispatch(applyNoticeToggle({ show: false }));
    applyLogoColor(params);
  }
};

export const applyLogoColor = (params) => {
  const { dispatch, canvasKey, sidebarTabKey, studioItemType, selectedLogo } = params;
  dispatch(
    tooManyColorsExceptionCheck(async (res) => {
      if (res.exception) {
        dispatch(applyNoticeToggle({ show: true, instance: res.message }));
      } else {
        dispatch(applyNoticeToggle({ show: false }));
        dispatch(selectImageForNonDigital({ canvasKey, sidebarTabKey, guid: selectedLogo.ui.guid }));

        let needSharpen =
          studioItemType === studioItemTypeEnum.screenPrintedDigital &&
          !isVectorImage(selectedLogo.data.fileName) &&
          (Math.ceil(selectedLogo.data.logoResolutionX < MIN_CANVAS_DPI) ||
            Math.ceil(selectedLogo.data.logoResolutionY < MIN_CANVAS_DPI))
            ? true
            : false;
        needSharpen = needSharpen || selectedLogo.data.sharpenStatus === imageSharpenStatus.Unsharpened;

        if (needSharpen) {
          await sharpenImage(params);
        } else {
          setLogoEditingStatusForColorize(dispatch, studioItemType, true, params.isNextgenUi);
        }
        if (isMobileOnly) dispatch(mobileStep2GuideNext());
      }
    })
  );
};

// sharpenImage is a private method, called by applyLogoColor
const sharpenImage = async (params) => {
  const { dispatch, canvasKey, studioItemType, selectedLogo, guid } = params;
  dispatch(changeImageUiStatus({ canvasKey, guid, unSharpenImageData: selectedLogo.data }));

  dispatch(showWaitingCover());
  dispatch(
    updateWaitingCover({
      text: 'Sharpening Image',
      textStyle: isMobileOnly ? 'waitingcover-text-sharpen-mobile' : 'waitingcover-text-sharpen',
      forceShowWaitingCover: true,
    })
  );

  params.callback = (newImageData) => {
    switchToSharpened(newImageData); // must call switchToSharpened because newImageData is actually from backend
    dispatch(changeImageUiStatus({ canvasKey, guid, sharpenedImageData: newImageData }));
    setLogoEditingStatusForColorize(dispatch, studioItemType, true, params.isNextgenUi);
    dispatch(hideWaitingCover({ reset: true }));
  };

  // clear ImagingServiceSessionId to make sure the colorized image is used
  selectedLogo.data = Object.assign({}, selectedLogo.data);
  selectedLogo.data.imagingServiceSessionIdOfNoColorize = selectedLogo.data.imagingServiceSessionId;
  selectedLogo.data.imagingServiceSessionIdListOfNoColorize = Object.assign(
    [],
    selectedLogo.data.imagingServiceSessionIdList
  );
  selectedLogo.data.imagingServiceSessionId = null;
  selectedLogo.data.imagingServiceSessionIdList = Object.assign([], selectedLogo.data.imagingServiceSessionIdList);
  selectedLogo.data.imagingServiceSessionIdList[selectedLogo.data.removeWhiteOption] = null;
  params.imageData = selectedLogo.data;
  params.keepCover = true;
  await doSharpen(params);
};

export const doReset = (params) => {
  const { dispatch, canvasKey, image, studioItemType } = params;

  resetToOriginal(params);

  dispatch(
    changeLogoStatus({
      canvasKey,
      imageId: image.ui.guid,
      status:
        studioItemType === studioItemTypeEnum.nonDigital ? elementStatus.TempSelected : elementStatus.SingleSelected,
    })
  );
  dispatch(changeLogoEditingStatus({ forceHideApplyNotice: true }));
};

export const checkIsNewUploadLogo = (selectedLogo) => {
  return (
    selectedLogo &&
    selectedLogo.data &&
    selectedLogo.data.imagingServiceSessionIdList.some((imagingServiceSessionId) => imagingServiceSessionId)
  );
};

export const doColorizeForScreenPrintedEditColor = (params) => {
  const { dispatch, canvasKey, vendorId, imprintColorList, selectedLogo, maxColorCount, itemUseScene7Template } =
    params;

  const finalize = (logoData) => {
    const newLogoData = updateLogoColorType(logoData, imprintColorList);

    dispatch(
      changeLogoColorAsync(
        {
          formatkey: canvasKey,
          guid: selectedLogo.ui.guid,
          colors: newLogoData.colors,
        },
        () => {
          //dispatch(saveImageOriginColors({ canvasKey, imageId: selectedLogo.ui.guid }));
        }
      )
    );
    if (params.switchToLogoColor) {
      dispatch(changeLogoStatus({ canvasKey, imageId: selectedLogo.ui.guid, status: elementStatus.TempSelected }));
      dispatch(changeLogoEditingStatus(logoEditingStatusEnum.NonDigitalLogoColor));
      dispatch(changeNextgenUiStatus({ flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.LogoColorPanel } }));
    } else {
      dispatch(changeImageUiStatus({ canvasKey, guid: selectedLogo.ui.guid, unSharpenImageData: newLogoData }));
    }
    hideCover(dispatch);
    if (params.needPushToUndo) dispatch(pushToUndoList(undoRedoType.ChangeTemplateOrEditElement));
  };

  const newSelectLogo = Object.assign({}, selectedLogo);
  newSelectLogo.data = Object.assign({}, newSelectLogo.data);

  switchToUnColorize(newSelectLogo.data);
  dispatch(updateCanvasImage({ canvasKey, imageId: newSelectLogo.ui.guid, newImageData: newSelectLogo.data }));

  const currentColorList = newSelectLogo.data.colors.map((color) => {
    return Object.assign({}, color);
  });
  const combinedColorList = currentColorList.concat(
    imprintColorList.map((color) => {
      return Object.assign({}, color);
    })
  );

  dispatch(showTransparencyCover());
  dispatch(changeNextgenUiStatus({ showCanvasCover: true }));

  colorize({
    dispatch,
    canvasKey,
    vendorId,
    image: newSelectLogo,
    imprintColorList: combinedColorList,
    maxColorCount,
    currentColorCount: newSelectLogo.data.colors.length,
    callback: finalize,
    itemUseScene7Template,
  });
};

export const initLogoFromRecent = ({ dispatch, vendorId, formatkey, tempLogo, callback }) => {
  dispatch(showWaitingCover({ showProgress: false }));

  post(API_EDIT_LOGO + '/initrecentlogo?vendorId=' + vendorId, tempLogo.data)
    .then((res) => {
      const newRecentLogo = res.data;
      dispatch(
        updateCanvasImage({
          canvasKey: formatkey,
          imageId: tempLogo.ui.guid,
          newImageData: newRecentLogo,
        })
      );
      if (callback) callback(newRecentLogo);
      dispatch(hideWaitingCover());
    })
    .catch((err) => {
      consoleLog(err);
      dispatch(hideWaitingCover());
      dispatch(showMessageBox(err.message));
    });
};

export const setLogoEditingStatusForColorize = (dispatch, studioItemType, isApplyColor, isNextgenUi) => {
  if (isApplyColor) {
    if (studioItemType === studioItemTypeEnum.screenPrintedDigital) {
      if (isMobileOnly) {
        dispatch(changeLogoEditingStatus(logoEditingStatusEnum.None));
      } else {
        dispatch(changeLogoEditingStatus(logoEditingStatusEnum.ScreenPrintedEditUpload));
      }
      if (isNextgenUi) {
        dispatch(
          changeNextgenUiStatus({
            flyoutPanel: {
              currentPanel:
                getScreenMode() === nextgenScreenMode.small
                  ? nextGenFlyoutPanelEnum.None
                  : nextGenFlyoutPanelEnum.EditImagePanel,
            },
          })
        );
      }
    } else {
      dispatch(changeLogoEditingStatus(logoEditingStatusEnum.NonDigitalLogoPreview));
    }
  } else {
    if (isMobileOnly && studioItemType === studioItemTypeEnum.screenPrintedDigital) {
      dispatch(changeLogoEditingStatus(logoEditingStatusEnum.None));
    } else {
      dispatch(changeLogoEditingStatus(logoEditingStatusEnum.NonDigitalLogoPreview));
    }
    if (isNextgenUi) {
      dispatch(
        changeNextgenUiStatus({
          flyoutPanel: {
            currentPanel:
              getScreenMode() === nextgenScreenMode.small
                ? nextGenFlyoutPanelEnum.None
                : nextGenFlyoutPanelEnum.EditImagePanel,
          },
        })
      );

      if (studioItemType !== studioItemTypeEnum.screenPrintedDigital) dispatch(pushToUndoList(undoRedoType.UploadLogo));
    }
  }
};

// this function will be called just after Logo Upload(for full preview)
// also be called in the potential first step(LogoRemoveWhite or ImageCropContainer)
// because logo colors maybe cleared in Reset/RecentArt
export const checkAndSetLogoColorBeforeColorize = ({
  dispatch,
  canvasKey,
  currentLogo,
  studioItemType,
  maxColorCount,
  imprintColors,
  textColor,
  defaultImprintColor,
}) => {
  if (!currentLogo) {
    return;
  }
  if (studioItemType === studioItemTypeEnum.digital) {
    return;
  }
  if (maxColorCount !== 1) {
    return;
  } // only for single color item
  if (currentLogo.data.colors && currentLogo.data.colors.length > 0) {
    return;
  } // skip if logo color already exists

  // use the only color in imprintColor list
  if (imprintColors && imprintColors.length === 1) {
    currentLogo.data.colors = [imprintColors[0]];
  } else if (textColor) {
    // use text color if exists
    currentLogo.data.colors = [textColor];
  } else if (defaultImprintColor) {
    // use default imprint color if exists
    currentLogo.data.colors = [defaultImprintColor];
  } else {
    // use the first available imprint color
    if (imprintColors && imprintColors.length > 1) {
      currentLogo.data.colors = [imprintColors[0]];
    }
  }

  // update the logo color to Store because Reset/Recent may clear it
  if (dispatch) {
    dispatch(changeArtColor({ canvasKey, guid: currentLogo.ui.guid, colors: currentLogo.data.colors }));
  }
};

export const adjustLogoOriginSizeByResolution = (newImageData, previewResolution) => {
  return {
    width: Math.round(
      (previewResolution / MIN_CANVAS_DPI) * (newImageData.autoCropRect.width || newImageData.finalCropRect.width)
    ),
    height: Math.round(
      (previewResolution / MIN_CANVAS_DPI) * (newImageData.autoCropRect.height || newImageData.finalCropRect.height)
    ),
  };
};

export const getCropParameterForCropContainer = ({
  currentStatus,
  imageData,
  resizeRatio,
  canvasScale,
  itemUseScene7Template,
  canvasData,
}) => {
  let cropParam = null;
  if (currentStatus === logoEditingStatusEnum.LogoCropping) {
    cropParam = {
      x: imageData.cropRect.x * resizeRatio * canvasScale,
      y: imageData.cropRect.y * resizeRatio * canvasScale,
      width: imageData.cropRect.width * resizeRatio * canvasScale,
      height: imageData.cropRect.height * resizeRatio * canvasScale,
    };
  } else if (currentStatus === logoEditingStatusEnum.NonDigitalLogoCrop) {
    cropParam = {
      x: 0,
      y: 0,
      width: imageData.autoCropRect.width * resizeRatio,
      height: imageData.autoCropRect.height * resizeRatio,
    };
  } else if (itemUseScene7Template) {
    // background crop using templatebasic - decide if auto crop in order to keep canvas ratio
    const containerSize = { width: canvasData.fullWidth, height: canvasData.fullHeight };
    const newImageSize = adjustImageSizeToOccupyContainer(
      { width: imageData.originalWidth, height: imageData.originalHeight },
      containerSize
    );
    if (newImageSize.width > containerSize.width) {
      const width = (containerSize.width / newImageSize.width) * imageData.originalWidth;
      cropParam = {
        x: ((imageData.originalWidth - width) / 2) * resizeRatio,
        y: 0,
        width: width * resizeRatio,
        height: imageData.originalHeight * resizeRatio,
      };
    } else if (newImageSize.height > containerSize.height) {
      const height = (containerSize.height / newImageSize.height) * imageData.originalHeight;
      cropParam = {
        x: 0,
        y: ((imageData.originalHeight - height) / 2) * resizeRatio,
        width: imageData.originalWidth * resizeRatio,
        height: height * resizeRatio,
      };
    }
  }
  return cropParam;
};

const adjustImageSizeToOccupyContainer = (imageSize, containerSize) => {
  if (typeof imageSize.height === 'undefined' || isNaN(imageSize.height) || imageSize.height === 0)
    return containerSize;

  let newWidth = 0.0;
  let newHeight = 0.0;
  const ratio = imageSize.width / imageSize.height;
  if (containerSize.width > containerSize.height) {
    newHeight = containerSize.height;
    newWidth = containerSize.height * ratio;
    if (newWidth < containerSize.width) {
      newWidth = containerSize.width;
      newHeight = containerSize.width / ratio;
    }
  } else {
    newWidth = containerSize.width;
    newHeight = containerSize.width / ratio;
    if (newHeight < containerSize.height) {
      newHeight = containerSize.height;
      newWidth = containerSize.height * ratio;
    }
  }

  return { width: Math.round(newWidth), height: Math.round(newHeight) };
};

function extractDominantColors(imageUrl, imageSize, numColors = 50, sampleSize = 500) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Downsample for performance
  const aspectRatio = imageSize.width / imageSize.height;
  let width = sampleSize;
  let height = sampleSize;
  if (aspectRatio > 1) {
    height = Math.round(width / aspectRatio);
  } else {
    width = Math.round(height * aspectRatio);
  }
  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(imageUrl, 0, 0, width, height);

  const imageData = ctx.getImageData(0, 0, width, height);
  const pixels = imageData.data;

  // Extract RGB values, ignoring transparent pixels
  const rgbPixels = [];

  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    const a = pixels[i + 3];
    if (a > 128) {
      // Skip mostly transparent pixels
      rgbPixels.push([r, g, b]);
    }
  }

  return kMeansClustering(rgbPixels, numColors);
}

function kMeansClustering(pixels, k, maxIterations = 10) {
  if (pixels.length === 0) return [];

  // Initialize centroids randomly
  let centroids = [];
  for (let i = 0; i < k; i++) {
    centroids.push(pixels[Math.floor(Math.random() * pixels.length)].slice());
  }

  const euclideanDistance = (a, b) => {
    return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);
  };

  for (let iter = 0; iter < maxIterations; iter++) {
    // Assign pixels to nearest centroid
    const clusters = Array.from({ length: k }, () => []);
    pixels.forEach((pixel) => {
      let minDist = Infinity;
      let closest = 0;
      centroids.forEach((centroid, idx) => {
        const dist = euclideanDistance(pixel, centroid);
        if (dist < minDist) {
          minDist = dist;
          closest = idx;
        }
      });
      clusters[closest].push(pixel);
    });

    // Update centroids
    centroids = centroids.map((_, idx) => {
      const cluster = clusters[idx];
      if (cluster.length === 0) return centroids[idx]; // No change if empty
      return cluster[0].map((_, i) => Math.round(cluster.reduce((sum, p) => sum + p[i], 0) / cluster.length));
    });
  }

  // Aggregate centroids with same color
  centroids = centroids.reduce((pre, cur) => {
    const currentIndex = pre.findIndex((el) => euclideanDistance(el, cur) < 50);
    if (currentIndex === -1) {
      return [...pre, cur];
    } else {
      return pre;
    }
  }, []);

  pixels.forEach((pixel) => {
    let minDist = Infinity;
    let closest = 0;
    centroids.forEach((centroid, idx) => {
      const dist = euclideanDistance(pixel, centroid);
      if (dist < minDist) {
        minDist = dist;
        closest = idx;
      }
    });
    centroids[closest][3] = (centroids[closest][3] || 0) + 1; // count
  });
  centroids = centroids.filter((item) => item[3] / pixels.length > 0.008);

  const toHex = (num) => num.toString(16).padStart(2, '0');
  return centroids.map((rgb) => `#${toHex(rgb[0])}${toHex(rgb[1])}${toHex(rgb[2])}`);
}
